/**
 * CLIENTS — the firm's own record of who it acts for.
 *
 * A ruled list, and a line to add to it. Not a CRUD app: there are no cards,
 * no tiles, no per-row menu of verbs, and nothing here can be edited or
 * deleted because the API has no PUT, PATCH or DELETE. A client is added and
 * then it exists; what happens to it happens in the work.
 */
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ClientListResponse, ClientResponse, type Client } from "@leveri/contracts";
import {
  Button, FileTitle, Notice, Reading, RuledList, RuledRow, Section, SectionHead, TextField,
} from "../file/index.js";
import { call, failureMessage } from "../api.js";
import { Desk } from "./shell.js";
import { useVocabularies, word } from "./vocabularies.js";

export function ClientsScreen() {
  const navigate = useNavigate();
  // The words for a client's status are the server's, fetched with the list.
  const { terms } = useVocabularies();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addFailure, setAddFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      setClients((await call("GET", "/api/clients", ClientListResponse)).clients);
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (adding) return;
    const form = event.currentTarget;
    const name = String(new FormData(form).get("name") ?? "");
    setAdding(true);
    setAddFailure(null);
    try {
      await call("POST", "/api/clients", ClientResponse, { name });
      form.reset();
      // The list is re-read rather than appended to: what the firm holds is
      // the server's answer, not this screen's idea of it plus one.
      await load();
    } catch (error) {
      setAddFailure(failureMessage(error));
    } finally {
      setAdding(false);
    }
  }

  return (
    <Desk current="clients" measure="wide">
      <FileTitle kicker="Your firm" size={28} meta="Who the firm acts for. Work is opened against a client's engagement.">
        Clients
      </FileTitle>

      <Section>
        <SectionHead aside={clients ? String(clients.length) : null}>The list</SectionHead>
        {failure && (
          <>
            <Notice>{failure}</Notice>
            <button type="button" onClick={() => void load()} className="mt-2 text-[13px] text-stone hover:text-ink">
              Try again
            </button>
          </>
        )}
        {!clients && !failure && <Reading>Loading the clients…</Reading>}
        {clients && clients.length === 0 && !failure && (
          <p className="text-sm text-stone">No clients yet. Add the first one below.</p>
        )}
        {clients && clients.length > 0 && (
          <RuledList>
            {clients.map((client) => (
              <RuledRow as="button" key={client.id} className="px-3" onClick={() => navigate(`/clients/${client.id}`)}>
                <div className="grid grid-cols-1 items-baseline gap-x-6 gap-y-1 md:grid-cols-[minmax(0,1fr)_auto]">
                  <span className="truncate text-sm font-medium text-ink">{client.name}</span>
                  <span
                    className="text-[13px] text-stone [font-variant-numeric:tabular-nums]"
                    title={client.createdAt}
                  >
                    {word(terms?.clientStatuses, client.status)} · {client.createdAtText}
                  </span>
                </div>
              </RuledRow>
            ))}
          </RuledList>
        )}
      </Section>

      <Section>
        <SectionHead>Add a client</SectionHead>
        <form onSubmit={add} className="flex flex-wrap items-end gap-4">
          <div className="md:w-[320px]"><TextField name="name" label="Name" required disabled={adding} /></div>
          <Button type="submit" disabled={adding}>Add the client</Button>
        </form>
        {adding && <div className="mt-2"><Reading>Adding the client…</Reading></div>}
        {addFailure && <div className="mt-2"><Notice>{addFailure}</Notice></div>}
      </Section>
    </Desk>
  );
}
